// src/pages/DebtStudio/components/DealForm/DealTerms.jsx

import React, { useRef, useState, useEffect, useMemo } from "react";
import dayjs from "lib/dayjs";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import SectionCard from "components/SectionCard";
import { DateInput, NumberInput, DropdownSelect } from "components/InputStyled";
import { applyRefundingDateDefaults, deriveRefundingDateDefaults } from "pages/DebtStudio/lib/refundingDateDefaults";

/* ────────────────────────────────
   Small UI helpers
──────────────────────────────── */
function FieldMessages({ error, warnings = [] }) {
  const warnList = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
  if (!error && warnList.length === 0) return null;

  return (
    <div className="mt-1 space-y-1">
      {error ? (
        <div className="text-xs text-[hsl(var(--destructive))]">{error}</div>
      ) : null}
      {warnList.map((w, i) => (
        <div key={i} className="text-xs text-muted">
          {w}
        </div>
      ))}
    </div>
  );
}

function stepMonthsForFreq(freq) {
  const f = String(freq || "").toLowerCase();
  if (f.startsWith("month")) return 1;
  if (f.startsWith("quarter")) return 3;
  if (f.startsWith("semi")) return 6;
  if (f.startsWith("annual")) return 12;
  return 6;
}

function isValidDayjs(d) {
  return !!(d && typeof d.isValid === "function" && d.isValid());
}

function deriveLoanTermYears(datedDate, finalMaturityDate) {
  const start = datedDate ? dayjs(datedDate).startOf("day") : null;
  const end = finalMaturityDate ? dayjs(finalMaturityDate).startOf("day") : null;

  if (!isValidDayjs(start) || !isValidDayjs(end)) return "";
  if (!end.isAfter(start, "day")) return "";

  const wholeYears = end.diff(start, "year");
  const test = start.add(wholeYears, "year");

  if (test.isSame(end, "day")) return wholeYears;

  return Number((end.diff(start, "day") / 365.25).toFixed(2));
}

function computePrefillCapiDate({
  firstCouponDate,
  couponFrequency,
  firstPrincipalDate,
}) {
  if (!firstCouponDate) return "";

  const fc = dayjs(firstCouponDate).startOf("day");
  if (!isValidDayjs(fc)) return "";

  const step = stepMonthsForFreq(couponFrequency);

  if (!firstPrincipalDate) return fc.format("YYYY-MM-DD");

  const fp = dayjs(firstPrincipalDate).startOf("day");
  if (!isValidDayjs(fp)) return fc.format("YYYY-MM-DD");

  if (!fp.isAfter(fc, "day")) return "";

  let cur = fc;
  for (let guard = 0; guard < 5000; guard++) {
    const next = cur.add(step, "month");
    if (!next.isBefore(fp, "day")) return cur.format("YYYY-MM-DD");
    cur = next;
  }

  return "";
}

function isCouponDate({
  capiDate,
  firstCouponDate,
  finalMaturityDate,
  couponFrequency,
}) {
  if (!capiDate || !firstCouponDate) return false;

  const ci = dayjs(capiDate);
  const fc = dayjs(firstCouponDate);
  if (!isValidDayjs(ci) || !isValidDayjs(fc)) return false;

  const step = stepMonthsForFreq(couponFrequency);

  let cur = fc.startOf("day");
  const target = ci.startOf("day");

  const end = finalMaturityDate ? dayjs(finalMaturityDate).startOf("day") : target;
  const hardStop = end.isAfter(target) ? end : target;

  for (let guard = 0; guard < 5000; guard++) {
    if (cur.isSame(target, "day")) return true;
    if (cur.isAfter(hardStop, "day")) return false;
    cur = cur.add(step, "month");
  }

  return false;
}

export default function DealTerms({ isReadOnly: isReadOnlyProp } = {}) {
  const { form, setForm, isReadOnly: isReadOnlyCtx } = useDebtStudio();
  const isReadOnly = isReadOnlyProp ?? isReadOnlyCtx;

  const isLoan = String(form?.financingType || "").toLowerCase() === "loan";
  const refundingDateDefaults = useMemo(() =>
    String(form?.dealType || "").toLowerCase() === "refunding"
      ? deriveRefundingDateDefaults(form)
      : null,
  [form]);

  const isManualRefundingDate = (field) =>
    !!refundingDateDefaults?.[field] &&
    !!form?.[field] &&
    form[field] !== refundingDateDefaults[field];

  const refundingDateClass = (field) => isManualRefundingDate(field)
    ? "!text-blue-700 dark:!text-blue-400 !font-bold"
    : "";

  const resetRefundingDate = (field) => {
    const autoDate = refundingDateDefaults?.[field];
    if (!autoDate) return null;
    setForm((prev) => ({ ...prev, [field]: deriveRefundingDateDefaults(prev)?.[field] || autoDate }));
    return autoDate;
  };

  const deliveryEdited = useRef(false);
  const capiDateEdited = useRef(false);
  const loanTermEdited = useRef(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});

  const setNum = (k, v) => {
    if (isReadOnly) return;
    setForm({ ...form, [k]: v });
  };

  const setStr = (k, v) => {
    if (isReadOnly) return;

    if (k === "couponFrequency") {
      setForm((prev) => applyRefundingDateDefaults(prev, { ...prev, couponFrequency: v }));
      return;
    }

    if (k === "firstCallDate") {
      setForm({
        ...form,
        firstCallDate: v,
        firstCallDateToken: Date.now(),
      });
      return;
    }

    setForm({ ...form, [k]: v });
  };

  const handleCouponRateChange = (v) => {
    if (isReadOnly) return;

    setForm((prev) => ({
      ...prev,
      couponRate: v,
      couponRateToken: Date.now(),
    }));
  };

  const handleTermYearsChange = (v) => {
    if (isReadOnly) return;

    loanTermEdited.current = true;

    const years = Number(v);

    const updated = {
      ...form,
      loanTermYears: Number.isFinite(years) ? years : "",
    };

    const closingDate = form?.datedDate ? dayjs(form.datedDate) : null;

    if (isValidDayjs(closingDate) && years > 0) {
      updated.finalMaturityDate = closingDate
        .add(years, "year")
        .format("YYYY-MM-DD");
    }

    setForm(updated);
  };

  const handleFinalMaturityDateChange = (v) => {
    if (isReadOnly) return;

    const updated = { ...form, finalMaturityDate: v };

    if (isLoan) {
      updated.loanTermYears = deriveLoanTermYears(form?.datedDate, v);
    }

    setForm(updated);
  };

  const handleDatedDateChange = (v) => {
    if (isReadOnly) return;

    const updated = { ...form, datedDate: v };

    if (isLoan) {
      updated.deliveryDate = v;

      const years = Number(form?.loanTermYears);

      if (v && Number.isFinite(years) && years > 0) {
        updated.finalMaturityDate = dayjs(v)
          .add(years, "year")
          .format("YYYY-MM-DD");
      }

      setForm(updated);
      return;
    }

    if (!deliveryEdited.current || !form.deliveryDate) {
      updated.deliveryDate = v;
    }

    setForm(applyRefundingDateDefaults(form, updated));
  };

  const handleDeliveryDateChange = (v) => {
    if (isReadOnly) return;

    deliveryEdited.current = true;
    setForm((prev) => applyRefundingDateDefaults(prev, { ...prev, deliveryDate: v }));
  };

  const showCapISection = useMemo(() => {
    if (isLoan) return false;

    const t = String(form?.dealType || "").toLowerCase();
    return t === "new_money" || t === "combined" || t === "combination";
  }, [isLoan, form?.dealType]);

  const capiEnabled = !!form?.capiEnabled;

  useEffect(() => {
    if (isReadOnly) return;
    if (!showCapISection) return;
    if (!capiEnabled) return;
    if (form?.capiDate) return;
    if (capiDateEdited.current) return;

    const prefill = computePrefillCapiDate({
      firstCouponDate: form?.firstCouponDate,
      couponFrequency: form?.couponFrequency,
      firstPrincipalDate: form?.firstPrincipalDate,
    });

    if (prefill) {
      setForm((prev) => ({ ...prev, capiDate: prefill }));
    }
  }, [
    isReadOnly,
    showCapISection,
    capiEnabled,
    form?.capiDate,
    form?.firstCouponDate,
    form?.couponFrequency,
    form?.firstPrincipalDate,
    setForm,
  ]);

  useEffect(() => {
    if (isReadOnly) return;
    if (!isLoan) return;
    if (loanTermEdited.current) return;
    if (form?.loanTermYears !== "" && form?.loanTermYears != null) return;

    const derived = deriveLoanTermYears(
      form?.datedDate,
      form?.finalMaturityDate
    );

    if (derived === "") return;

    setForm((prev) => ({
      ...prev,
      loanTermYears: prev?.loanTermYears ?? derived,
    }));
  }, [
    isReadOnly,
    isLoan,
    form?.datedDate,
    form?.finalMaturityDate,
    form?.loanTermYears,
    setForm,
  ]);

  const computeChecks = () => {
    const errs = {};
    const warns = {};

    const addWarn = (k, msg) => {
      if (!msg) return;
      if (!warns[k]) warns[k] = [];
      warns[k].push(msg);
    };

    const dated = form?.datedDate ? dayjs(form.datedDate) : null;
    const delivery = form?.deliveryDate ? dayjs(form.deliveryDate) : null;
    const firstInt = form?.firstCouponDate ? dayjs(form.firstCouponDate) : null;
    const firstPrin = form?.firstPrincipalDate
      ? dayjs(form.firstPrincipalDate)
      : null;
    const finalMat = form?.finalMaturityDate
      ? dayjs(form.finalMaturityDate)
      : null;
    const firstCall = form?.firstCallDate
      ? dayjs(form.firstCallDate)
      : null;

    if (isValidDayjs(dated) && isValidDayjs(delivery)) {
      if (delivery.isBefore(dated, "day")) {
        errs.deliveryDate = isLoan
          ? "Closing Date must match settlement date."
          : "Delivery Date must be on or after Dated Date.";
      }
    }

    if (isValidDayjs(delivery) && isValidDayjs(firstInt)) {
      if (firstInt.isBefore(delivery, "day")) {
        errs.firstCouponDate = isLoan
          ? "First Interest Date must be on or after Closing Date."
          : "First Interest Date must be on or after Delivery Date.";
      }
    }

    if (isValidDayjs(dated) && isValidDayjs(firstInt)) {
      if (!firstInt.isAfter(dated, "day")) {
        errs.firstCouponDate =
          errs.firstCouponDate ||
          (isLoan
            ? "First Interest Date must be after Closing Date."
            : "First Interest Date must be after Dated Date.");
      }
    }

    if (isValidDayjs(firstInt) && isValidDayjs(finalMat)) {
      if (finalMat.isBefore(firstInt, "day")) {
        errs.finalMaturityDate =
          errs.finalMaturityDate ||
          "Final Maturity Date must be on or after First Interest Date.";
      }
    }

    if (isValidDayjs(firstPrin) && isValidDayjs(finalMat)) {
      if (finalMat.isBefore(firstPrin, "day")) {
        errs.finalMaturityDate =
          errs.finalMaturityDate ||
          "Final Maturity Date must be on or after First Principal Date.";
      }
    }

    if (isValidDayjs(firstPrin) && isValidDayjs(firstInt)) {
      if (firstPrin.isBefore(firstInt, "day")) {
        addWarn(
          "firstPrincipalDate",
          "First Principal Date is before the First Interest Date, this is unusual."
        );
      }
    }

    if (isValidDayjs(firstCall) && isValidDayjs(finalMat)) {
      if (!firstCall.isBefore(finalMat, "day")) {
        errs.firstCallDate =
          "First Call Date must be before the Final Maturity Date.";
      }
    }

    if (isValidDayjs(firstCall) && isValidDayjs(delivery)) {
      if (firstCall.isBefore(delivery, "day")) {
        addWarn(
          "firstCallDate",
          isLoan
            ? "First Call Date is before Closing Date, this is unusual."
            : "First Call Date is before Delivery Date, this is unusual."
        );
      }
    }

    if (showCapISection && capiEnabled) {
      const capiDateRaw = form?.capiDate || "";
      const capiDate = capiDateRaw ? dayjs(capiDateRaw) : null;

      if (!capiDateRaw) {
        errs.capiDate =
          "Cap-I Date is required when Capitalized Interest is enabled.";
      }

      if (isValidDayjs(capiDate) && isValidDayjs(firstPrin)) {
        if (!capiDate.isBefore(firstPrin, "day")) {
          errs.capiDate =
            "Cap-I Date must be before the First Principal Date.";
        }
      }

      if (isValidDayjs(capiDate) && isValidDayjs(firstInt)) {
        if (capiDate.isBefore(firstInt, "day")) {
          addWarn(
            "capiDate",
            "Cap-I Date is before the First Interest Date, Capitalized Interest may have no effect."
          );
        }
      }

      if (isValidDayjs(capiDate) && isValidDayjs(delivery)) {
        if (capiDate.isBefore(delivery, "day")) {
          addWarn(
            "capiDate",
            "Cap-I Date is before Delivery Date, Capitalized Interest may have no effect."
          );
        }
      }

      if (isValidDayjs(capiDate) && isValidDayjs(firstInt)) {
        const onCycle = isCouponDate({
          capiDate: capiDate.format("YYYY-MM-DD"),
          firstCouponDate: firstInt.format("YYYY-MM-DD"),
          finalMaturityDate: form?.finalMaturityDate || "",
          couponFrequency: form?.couponFrequency,
        });

        if (!onCycle) {
          addWarn(
            "capiDate",
            "Cap-I Date is not on a coupon payment date, this can be confusing."
          );
        }
      }

      const r = Number(form?.capiInvestmentRate);

      if (Number.isFinite(r)) {
        if (r < 0) {
          errs.capiInvestmentRate =
            "Cap-I Investment Rate cannot be negative.";
        } else if (r > 20) {
          addWarn(
            "capiInvestmentRate",
            "Cap-I Investment Rate is unusually high."
          );
        }
      }
    }

    setFieldErrors(errs);
    setFieldWarnings(warns);
  };

  useEffect(() => {
    computeChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.datedDate,
    form.deliveryDate,
    form.firstCouponDate,
    form.firstPrincipalDate,
    form.finalMaturityDate,
    form.firstCallDate,
    form.dealType,
    form.financingType,
    form.loanTermYears,
    form.couponFrequency,
    form.principalFrequency,
    form.capiEnabled,
    form.capiDate,
    form.capiInvestmentRate,
  ]);

  return (
    <div className="space-y-4">
      <SectionCard title="Dates">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <DateInput
              disabled={isReadOnly}
              label={
                isLoan
                  ? "Origination/Closing Date"
                  : "Dated Date (Int Accrual Dt)"
              }
              value={form.datedDate}
              onChange={handleDatedDateChange}
            />
          </div>

          {!isLoan ? (
            <div>
              <DateInput
                disabled={isReadOnly}
                label="Delivery Date (Settlement Dt)"
                value={form.deliveryDate}
                onChange={handleDeliveryDateChange}
              />
              <FieldMessages
                error={fieldErrors.deliveryDate}
                warnings={fieldWarnings.deliveryDate}
              />
            </div>
          ) : null}

          {isLoan ? (
            <div>
              <NumberInput
                disabled={isReadOnly}
                label="Term (Years)"
                value={form.loanTermYears ?? ""}
                onChange={handleTermYearsChange}
                type="decimal"
                precision={0}
                min="0"
                className="w-full"
              />
            </div>
          ) : null}

          <div>
            <DateInput
              disabled={isReadOnly}
              label="First Interest Date"
              value={form.firstCouponDate}
              onChange={(v) => setStr("firstCouponDate", v)}
              onClear={refundingDateDefaults?.firstCouponDate
                ? () => resetRefundingDate("firstCouponDate") : undefined}
              className={`w-full ${refundingDateClass("firstCouponDate")}`}
            />
            <FieldMessages
              error={fieldErrors.firstCouponDate}
              warnings={fieldWarnings.firstCouponDate}
            />
            {refundingDateDefaults?.ambiguousFirstCouponDate && (
              <p className="mt-1 text-xs text-muted">Selected maturities have different coupon calendars; enter the first interest date.</p>
            )}
          </div>

          <div>
            <DateInput
              disabled={isReadOnly}
              label="First Principal Date"
              value={form.firstPrincipalDate}
              onChange={(v) => setStr("firstPrincipalDate", v)}
              onClear={refundingDateDefaults?.firstPrincipalDate
                ? () => resetRefundingDate("firstPrincipalDate") : undefined}
              className={`w-full ${refundingDateClass("firstPrincipalDate")}`}
            />
            <FieldMessages
              error={fieldErrors.firstPrincipalDate}
              warnings={fieldWarnings.firstPrincipalDate}
            />
          </div>

          <div>
            <DateInput
              disabled={isReadOnly}
              label="Final Maturity Date"
              value={form.finalMaturityDate}
              onChange={handleFinalMaturityDateChange}
              onClear={refundingDateDefaults?.finalMaturityDate
                ? () => resetRefundingDate("finalMaturityDate") : undefined}
              className={`w-full ${refundingDateClass("finalMaturityDate")}`}
            />
            <FieldMessages
              error={fieldErrors.finalMaturityDate}
              warnings={fieldWarnings.finalMaturityDate}
            />
          </div>

          {refundingDateDefaults && (
            <p className="md:col-span-3 text-xs text-muted">
              Blue bold dates are manual overrides. Clear a date to restore its value from the selected refunding maturities.
            </p>
          )}

          <div>
            <DateInput
              disabled={isReadOnly}
              label={
                isLoan
                  ? "Prepayment Option Date (optional)"
                  : "First Call Date (optional)"
              }
              value={form.firstCallDate}
              onChange={(v) => setStr("firstCallDate", v)}
            />
            <FieldMessages
              error={fieldErrors.firstCallDate}
              warnings={fieldWarnings.firstCallDate}
            />
          </div>

          <div>
            <DateInput
              disabled={isReadOnly}
              label="Fiscal Year End (optional)"
              value={form.fiscalYearEnd}
              onChange={(v) => setStr("fiscalYearEnd", v)}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Payment Frequencies">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DropdownSelect
            disabled={isReadOnly}
            label="Interest Frequency"
            value={form.couponFrequency}
            onChange={(v) => setStr("couponFrequency", v)}
            options={[
              { value: "annual", label: "Annual" },
              { value: "semiannual", label: "Semiannual" },
              { value: "quarterly", label: "Quarterly" },
              { value: "monthly", label: "Monthly" },
            ]}
          />

          <DropdownSelect
            disabled={isReadOnly}
            label="Principal Frequency"
            value={form.principalFrequency}
            onChange={(v) => setStr("principalFrequency", v)}
            options={[
              { value: "annual", label: "Annual" },
              { value: "semiannual", label: "Semiannual" },
              { value: "quarterly", label: "Quarterly" },
              { value: "monthly", label: "Monthly" },
            ]}
          />
        </div>
      </SectionCard>

      {showCapISection ? (
        <SectionCard title="Capitalized Interest">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DropdownSelect
              disabled={isReadOnly}
              label="Capitalized Interest"
              value={capiEnabled ? "true" : "false"}
              onChange={(v) => {
                if (isReadOnly) return;

                const enabled = String(v) === "true";

                if (!enabled) {
                  capiDateEdited.current = false;

                  setForm({
                    ...form,
                    capiEnabled: false,
                    capiDate: "",
                    capiInvestmentRate: "",
                  });
                } else {
                  const prefill =
                    form?.capiDate ||
                    computePrefillCapiDate({
                      firstCouponDate: form?.firstCouponDate,
                      couponFrequency: form?.couponFrequency,
                      firstPrincipalDate: form?.firstPrincipalDate,
                    }) ||
                    "";

                  setForm({
                    ...form,
                    capiEnabled: true,
                    capiDate: prefill,
                    capiInvestmentRate:
                      form.capiInvestmentRate ??
                      form.capiInvestmentRate === 0
                        ? form.capiInvestmentRate
                        : 0,
                  });
                }
              }}
              options={[
                { value: "false", label: "Off" },
                { value: "true", label: "On" },
              ]}
            />

            {capiEnabled ? (
              <div>
                <DateInput
                  disabled={isReadOnly}
                  label="Cap-I Date"
                  value={form.capiDate || ""}
                  onChange={(v) => {
                    capiDateEdited.current = true;
                    setStr("capiDate", v);
                  }}
                  maxDate={form.firstPrincipalDate || ""}
                />
                <FieldMessages
                  error={fieldErrors.capiDate}
                  warnings={fieldWarnings.capiDate}
                />
              </div>
            ) : null}

            {capiEnabled ? (
              <div>
                <NumberInput
                  disabled={isReadOnly}
                  label="Investment Rate"
                  value={form.capiInvestmentRate ?? ""}
                  onChange={(v) => setNum("capiInvestmentRate", v)}
                  type="percent"
                  className="w-full"
                />
                <FieldMessages
                  error={fieldErrors.capiInvestmentRate}
                  warnings={fieldWarnings.capiInvestmentRate}
                />
              </div>
            ) : null}
          </div>

          {capiEnabled ? (
            <div className="mt-2 text-sm text-muted">
              Interest payments to and including the Cap-I Date are drawn from
              the Cap-I fund. Cap-I assumes delayed principal repayment, so the
              Cap-I Date must be before the First Principal Date.
            </div>
          ) : null}
        </SectionCard>
      ) : null}

      <SectionCard title="Other Terms">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumberInput
            disabled={isReadOnly}
            label={isLoan ? "Interest Rate" : "Coupon"}
            value={form.couponRate}
            onChange={handleCouponRateChange}
            type="percent"
            precision={3}
            min="0"
            className="w-full"
          />

          <NumberInput
            disabled={isReadOnly}
            label={isLoan ? "Min. Increment" : "Principal Denomination"}
            value={form.denomination}
            onChange={(v) => setNum("denomination", v)}
            type="decimal"
            precision={2}
            min="0.01"
            className="w-full"
          />

          <DropdownSelect
            disabled={isReadOnly}
            label="Day Count"
            value={form.dayCount}
            onChange={(v) => setStr("dayCount", v)}
            options={[
              { value: "30/360", label: "30/360" },
              { value: "ACT/360", label: "ACT/360" },
              { value: "ACT/365", label: "ACT/365" },
              { value: "ACT/ACT", label: "ACT/ACT" },
            ]}
          />
        </div>
      </SectionCard>
    </div>
  );
}
